import { DetailedError } from 'tus-js-client';
import { UploadSDK } from '@yandex-int/video-upload-sdk';
import { VideosService } from '../../api/api-services/videos-service/videosService';
import { toastError } from '../toasts/toasts';
import { UPLOAD_URL_TTL } from '../../constants/UPLOAD_URL_TTL';
import { toastsT } from '../../i18n/toasts.i18n';

export type UploadStatus = 'error' | 'progress' | 'success' | 'resume';

export type UploadVideoParams = {
    file: File;
    videoTitle: string;
    profile: string;
    enableSummarization: boolean;
    onUploadStateChange: (props: {
        status?: UploadStatus;
        progress?: number;
        error?: Error | DetailedError;
        uploadUrl?: string;
    }) => void;
    onUploadStart?: (uploadUrl: string) => void;
    onAbort?: () => void;
};

export async function uploadVideo({
    file,
    videoTitle,
    profile,
    enableSummarization,
    onUploadStateChange,
    onUploadStart,
    onAbort,
}: UploadVideoParams): Promise<{
    abort: () => Promise<void>;
    start: () => Promise<void>;
} | null> {
    let { uploadedPreviously, uploadUrl, creationTime } = await UploadSDK.requestFileStatus(file);
    const uploadUrlExpired = !isUploadUrlValid(creationTime);

    if (uploadedPreviously && uploadUrlExpired) {
        UploadSDK.removeUpload(uploadUrl);
    }

    if (uploadedPreviously && !uploadUrlExpired) {
        onUploadStateChange({ status: 'resume' });
    }

    if (!uploadedPreviously || uploadUrlExpired) {
        try {
            uploadUrl = (
                await VideosService.createVideoDocument(
                    file,
                    videoTitle,
                    profile,
                    enableSummarization,
                )
            ).tusd_url;
        } catch (e) {
            toastError.open(toastsT('createVideoDocumentFailed'));
            return Promise.resolve(null);
        }
    }

    const uploadPromise = UploadSDK.upload({
        file,
        uploadUrl,
        metadata: {
            filename: file.name,
            filetype: file.type,
        },
        onError: function (error: Error | DetailedError) {
            onUploadStateChange({ status: 'error', error });
        },
        onProgress: function ({ percentage }) {
            onUploadStateChange({ status: 'progress', progress: Math.round(percentage) });
        },
        onSuccess: function () {
            onUploadStateChange({ status: 'success' });
        },
    });

    if (uploadPromise) {
        if (onUploadStart) {
            const originalStart = uploadPromise.start;
            uploadPromise.start = async () => {
                const startPromise = originalStart();

                await startPromise;

                onUploadStart(uploadUrl);
            };
        }

        if (onAbort) {
            const originalAbort = uploadPromise.abort;
            uploadPromise.abort = async () => {
                try {
                    await originalAbort();
                } catch (e) {
                    //
                }
                onAbort();
            };
        }
    }

    return uploadPromise;
}

function isUploadUrlValid(creationTime: string) {
    const creationTimestamp = new Date(creationTime).getTime();

    if (isNaN(creationTimestamp)) {
        return false;
    }

    return Date.now() <= creationTimestamp + UPLOAD_URL_TTL;
}
